import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  FiUsers, FiUserPlus, FiRefreshCw, FiAlertTriangle, FiZap,
  FiArrowRight, FiClock, FiCheckCircle, FiChevronRight, FiMinus, FiPlus,
} from 'react-icons/fi'
import { diagnose, type DiagnosisAnswer, type DiagnosedEducation } from './safetyEducationData'

const EMPTY: DiagnosisAnswer = {
  officeWorkers: 0, salesWorkers: 0, fieldWorkers: 0, supervisorCount: 0,
  newHires6mo: 0, jobChanges: 0, hasSpecialTask: false, hasChemical: false,
  isConstruction: false, hasForeign: false,
}

const PRIORITY_STYLE: Record<string, { label: string; cls: string }> = {
  urgent: { label: '긴급', cls: 'bg-red-500/15 text-red-300 border-red-500/40' },
  high: { label: '높음', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  medium: { label: '보통', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
  low: { label: '낮음', cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40' },
}

function Stepper({ label, icon, value, onChange }: {
  label: string; icon: React.ReactNode; value: number; onChange: (v: number) => void
}) {
  const set = (v: number) => onChange(Math.max(0, Math.min(999, v)))
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3">
      <span className="flex items-center gap-2 text-sm text-zinc-300">
        <span className="text-purple-300">{icon}</span>{label}
      </span>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => set(value - 1)}
          className="h-7 w-7 rounded-lg border border-white/10 text-zinc-400 hover:bg-white/5 flex items-center justify-center">
          <FiMinus size={13} />
        </button>
        <input
          type="number" value={value} min={0}
          onChange={(e) => set(parseInt(e.target.value || '0', 10))}
          className="w-12 bg-transparent text-center text-white font-bold text-base outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button type="button" onClick={() => set(value + 1)}
          className="h-7 w-7 rounded-lg border border-white/10 text-zinc-400 hover:bg-white/5 flex items-center justify-center">
          <FiPlus size={13} />
        </button>
      </div>
    </div>
  )
}

function Toggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-xl border px-3 py-2.5 text-sm text-left transition-colors ${
        active ? 'border-purple-500/60 bg-purple-500/15 text-white font-medium'
               : 'border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:border-white/20'}`}>
      <span className="flex items-center gap-2">
        <span className={`h-4 w-4 rounded-md border flex items-center justify-center shrink-0 ${
          active ? 'bg-purple-500 border-purple-500' : 'border-white/20'}`}>
          {active && <FiCheckCircle size={11} className="text-white" />}
        </span>
        {label}
      </span>
    </button>
  )
}

export default function LegalEducationDiagnosisWizard() {
  const [a, setA] = useState<DiagnosisAnswer>(EMPTY)
  const [result, setResult] = useState<ReturnType<typeof diagnose> | null>(null)
  const upd = (patch: Partial<DiagnosisAnswer>) => setA((p) => ({ ...p, ...patch }))
  const totalWorkers = a.officeWorkers + a.salesWorkers + a.fieldWorkers + a.supervisorCount

  const run = () => setResult(diagnose(a))
  const reset = () => { setA(EMPTY); setResult(null) }

  return (
    <div className="rounded-3xl border border-white/[0.10] bg-gradient-to-b from-purple-950/20 to-zinc-900/40 p-6 md:p-10 shadow-2xl">
      <AnimatePresence mode="wait">
        {!result ? (
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -8 }}>
            <div className="grid lg:grid-cols-2 gap-x-8 gap-y-5">
              <div>
                <h4 className="text-sm font-semibold text-purple-300 mb-3">① 근로자 구성 (명)</h4>
                <div className="space-y-2.5">
                  <Stepper label="사무직" icon={<FiUsers size={15} />} value={a.officeWorkers} onChange={(v) => upd({ officeWorkers: v })} />
                  <Stepper label="현장·생산직" icon={<FiUsers size={15} />} value={a.fieldWorkers} onChange={(v) => upd({ fieldWorkers: v })} />
                  <Stepper label="판매·서비스직" icon={<FiUsers size={15} />} value={a.salesWorkers} onChange={(v) => upd({ salesWorkers: v })} />
                  <Stepper label="관리감독자" icon={<FiUsers size={15} />} value={a.supervisorCount} onChange={(v) => upd({ supervisorCount: v })} />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-purple-300 mb-3">② 최근·예정 변동</h4>
                <div className="space-y-2.5">
                  <Stepper label="최근 6개월 신규 채용" icon={<FiUserPlus size={15} />} value={a.newHires6mo} onChange={(v) => upd({ newHires6mo: v })} />
                  <Stepper label="예정 작업변경 건수" icon={<FiRefreshCw size={15} />} value={a.jobChanges} onChange={(v) => upd({ jobChanges: v })} />
                </div>
                <h4 className="text-sm font-semibold text-purple-300 mt-5 mb-3">③ 해당 사항 (선택)</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Toggle label="유해·위험작업 종사" active={a.hasSpecialTask} onClick={() => upd({ hasSpecialTask: !a.hasSpecialTask })} />
                  <Toggle label="화학물질 취급(MSDS)" active={a.hasChemical} onClick={() => upd({ hasChemical: !a.hasChemical })} />
                  <Toggle label="건설업" active={a.isConstruction} onClick={() => upd({ isConstruction: !a.isConstruction })} />
                  <Toggle label="외국인 근로자" active={a.hasForeign} onClick={() => upd({ hasForeign: !a.hasForeign })} />
                </div>
              </div>
            </div>
            <div className="mt-7 flex flex-col sm:flex-row items-center gap-3">
              <button onClick={run} disabled={totalWorkers === 0}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed px-7 py-3.5 text-white font-bold text-base transition-colors">
                <FiZap size={18} /> 우리 회사 의무 교육·과태료 진단
              </button>
              <span className="text-xs text-zinc-500">
                {totalWorkers === 0 ? '근로자 수를 1명 이상 입력하세요' : `입력 근로자 ${totalWorkers}명 · 30초면 끝납니다`}
              </span>
            </div>
          </motion.div>
        ) : (
          <motion.div key="result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <ResultView result={result} foreign={a.hasForeign} onReset={reset} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function ResultView({ result, foreign, onReset }: {
  result: ReturnType<typeof diagnose>; foreign: boolean; onReset: () => void
}) {
  const { educations, totalPenaltyKrw } = result
  if (educations.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-zinc-300">{result.summary}</p>
        <button onClick={onReset} className="mt-4 text-purple-300 hover:underline text-sm">다시 진단하기</button>
      </div>
    )
  }
  const eligible = educations.filter((e) => e.aiEligible)
  return (
    <div>
      {/* 과태료 후크 */}
      <div className="rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-500/12 to-transparent p-6 mb-6 text-center">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-300 uppercase tracking-widest">
          <FiAlertTriangle size={13} /> 미실시 시 과태료 노출 (1차 적발, 별표 35)
        </div>
        <div className="text-4xl md:text-5xl font-black text-red-400 mt-2 tabular-nums">
          약 {totalPenaltyKrw.toLocaleString()}원
        </div>
        <p className="text-sm text-zinc-300 mt-2">
          귀 사업장은 <b className="text-white">{educations.length}종 법정 교육 의무</b>가 있습니다.
          이 중 <b className="text-purple-300">{eligible.length}종</b>을 Heinrich AI로 즉시 생성·대체할 수 있습니다.
        </p>
      </div>

      {/* 교육별 카드 */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        {educations.map((e) => <EduCard key={e.code} e={e} />)}
      </div>

      {foreign && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/8 px-4 py-3 mb-6 text-sm text-sky-200 flex items-center gap-2">
          🌏 외국인 근로자 보유 — 베트남어·중국어·인도네시아어 등 <b>모국어 자막·음성 교육</b>을 함께 생성하면 이해도·증빙이 강화됩니다.
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Link to="/dashboard/safety-education"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 px-7 py-3.5 text-white font-bold transition-colors">
          이 교육들 지금 무료로 생성 <FiArrowRight size={18} />
        </Link>
        <button onClick={onReset} className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1">
          <FiRefreshCw size={13} /> 다시 진단
        </button>
      </div>
    </div>
  )
}

function EduCard({ e }: { e: DiagnosedEducation }) {
  const ps = PRIORITY_STYLE[e.priority]
  return (
    <div className={`rounded-xl border p-4 ${e.aiEligible ? 'border-white/[0.10] bg-white/[0.02]' : 'border-zinc-700/50 bg-zinc-800/20'}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="font-semibold text-white text-[15px] leading-snug">{e.nameKo}</div>
        <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${ps.cls}`}>{ps.label}</span>
      </div>
      <div className="text-xs text-zinc-400 leading-relaxed mb-2.5">{e.reason}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-500">
        <span className="flex items-center gap-1"><FiClock size={11} /> {e.minimumHours}h · {e.periodicity}</span>
        <span>대상 {e.targetWorkers}명</span>
        {e.aiEligible
          ? <span className="text-purple-300 font-medium">AI 영상 {e.aiEpisodes}편{e.instructorHours > 0 ? ` + 강사 ${e.instructorHours}h` : ''}</span>
          : <span className="text-amber-400 font-medium">집체 의무 · AI 대체 불가</span>}
        <span className="text-red-400 font-medium">미실시 {e.penaltyKrw.toLocaleString()}원</span>
      </div>
      {!e.aiEligible && (
        <div className="mt-2 text-[11px] text-amber-300/80 flex items-center gap-1">
          <FiChevronRight size={11} /> 등록 교육기관 위탁 필요 (Heinrich 자동 차단 항목)
        </div>
      )}
    </div>
  )
}
